import type { ApiApplicationStage } from '../../libs/api/pipelineService';

export const STAGES: ApiApplicationStage[] = [
  'NEW',
  'SCREENING',
  'INTERVIEW',
  'OFFER',
  'HIRED',
  'REJECTED',
];

export const STAGE_LABELS: Record<ApiApplicationStage, string> = {
  NEW: 'New',
  SCREENING: 'Screening',
  INTERVIEW: 'Interview',
  OFFER: 'Offer',
  HIRED: 'Hired',
  REJECTED: 'Rejected',
};

export type PipelineCard = {
  id: string;
  applicationId: string;
  candidateId: string;
  name: string;
  email: string;
  emailSentAt?: string | null;
  canSendRejectionEmail?: boolean;
  role: string;
  department: string | null;
  stage: ApiApplicationStage;
  score?: number;
  nextInterviewId?: string | null;
  nextInterviewResult?: 'PASSED' | 'FAILED' | null;
  nextInterviewRound?: string | null;
  appliedDate: string;
  kanbanNote?: string;
  noteId?: string;
};

export type ColumnState = Record<ApiApplicationStage, PipelineCard[]>;

export const createEmptyColumns = (): ColumnState => ({
  NEW: [],
  SCREENING: [],
  INTERVIEW: [],
  OFFER: [],
  HIRED: [],
  REJECTED: [],
});

const normalizeSearchText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .trim();

export type PipelineBoardFilters = {
  search: string;
  jobTitles: string[];
  teams: string[];
};

export const isPipelineFilterActive = (filters: PipelineBoardFilters) =>
  filters.search.trim().length > 0 || filters.jobTitles.length > 0 || filters.teams.length > 0;

export const filterPipelineColumns = (
  columns: ColumnState,
  filters: PipelineBoardFilters,
): ColumnState => {
  if (!isPipelineFilterActive(filters)) {
    return columns;
  }

  const normalizedSearch = normalizeSearchText(filters.search);
  const next = createEmptyColumns();

  STAGES.forEach((stage) => {
    next[stage] = columns[stage].filter((card) => {
      const matchesSearch =
        !normalizedSearch ||
        normalizeSearchText(card.name).includes(normalizedSearch) ||
        normalizeSearchText(card.email).includes(normalizedSearch);

      const matchesJobTitle =
        filters.jobTitles.length === 0 || filters.jobTitles.includes(card.role);

      const matchesTeam =
        filters.teams.length === 0 ||
        (card.department != null && filters.teams.includes(card.department));

      return matchesSearch && matchesJobTitle && matchesTeam;
    });
  });

  return next;
};

export const collectUniqueValues = (
  columns: ColumnState,
  pick: (card: PipelineCard) => string | null | undefined,
) => {
  const values = new Set<string>();

  STAGES.forEach((stage) => {
    columns[stage].forEach((card) => {
      const value = pick(card);
      if (value) {
        values.add(value);
      }
    });
  });

  return Array.from(values).sort((a, b) => a.localeCompare(b));
};

export const countCards = (columns: ColumnState) =>
  STAGES.reduce((total, stage) => total + columns[stage].length, 0);
